import { IconButton as MuiIconButton } from "@mui/material";
import { styled, type CSSObject, type Theme } from "@mui/material/styles";

import { type ButtonSize, type ButtonVariant, type IconButtonProps } from "ui/button/button.types";

import { ICON_BUTTON_PADDING } from "theme";

type IconButtonColor = NonNullable<IconButtonProps["color"]>;

type Paint = { main: string; dark: string; contrastText: string };

const PALETTE_COLORS = ["primary", "secondary", "error", "info", "success", "warning"] as const;

type PaletteColorName = (typeof PALETTE_COLORS)[number];

const isPaletteColor = (color: IconButtonColor): color is PaletteColorName =>
  (PALETTE_COLORS as readonly string[]).includes(color);

/** `inherit` — and MUI's `default`, which the design has no cell for — take the neutral paint. */
const paintFor = (theme: Theme, color: IconButtonColor): Paint =>
  isPaletteColor(color)
    ? theme.palette[color]
    : {
        main: theme.palette.grey[600],
        dark: theme.palette.grey[900],
        contrastText: theme.palette.common.white,
      };

const RADIUS = { small: "small", medium: "medium", large: "medium" } as const;

/**
 * `text` keeps MUI's circle: it is the shape of the hover disc (`--IconButton-hoverBg`), which a text icon
 * button keeps. The two painted surfaces take Figma's rounded square.
 */
const radiusStyle = (theme: Theme, variant: ButtonVariant, size: ButtonSize): CSSObject =>
  variant === "text" ? {} : { borderRadius: theme.shape[RADIUS[size]] };

/** The stroke is drawn inside the square, so the padding gives its pixel back. */
const OUTLINED_BORDER = 1;

/** `props.size` is typed with MUI's override union, so an unknown value falls back to medium. */
const sizeName = (size: string | undefined): ButtonSize =>
  size === "small" || size === "large" ? size : "medium";

/**
 * MUI's `IconButton` has no `variant`, so the design's three surfaces are painted here. MUI hovers every
 * icon button with an `alpha(main, hoverOpacity)` disc through `--IconButton-hoverBg`. `text` keeps it;
 * the two painted surfaces restate their own hover fill, which the disc would otherwise replace.
 */
const surfaceStyles = (
  theme: Theme,
  paint: Paint,
  size: ButtonSize,
): Record<ButtonVariant, CSSObject> => ({
  contained: {
    color: paint.contrastText,
    backgroundColor: paint.main,
    boxShadow: theme.shadows[1],
    "&:hover": { backgroundColor: paint.dark },
    "&.Mui-focusVisible": { boxShadow: theme.shadows[2] },
    "&.Mui-disabled": {
      color: theme.palette.grey[500],
      backgroundColor: theme.palette.grey[100],
    },
  },
  outlined: {
    padding: `calc(${theme.spacing(ICON_BUTTON_PADDING[size])} - ${OUTLINED_BORDER}px)`,
    color: paint.main,
    backgroundColor: theme.palette.background.paper,
    border: `${OUTLINED_BORDER}px solid currentColor`,
    boxShadow: theme.shadows[1],
    "&:hover": { color: paint.dark, backgroundColor: theme.palette.background.paper },
    "&.Mui-focusVisible": { boxShadow: theme.shadows[2] },
    "&.Mui-disabled": {
      color: theme.palette.grey[500],
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.grey[200],
    },
  },
  text: {
    color: paint.main,
    "&:hover": { color: paint.dark },
    "&.Mui-disabled": { color: theme.palette.grey[500] },
  },
});

export const iconButtonStyle = (
  theme: Theme,
  variant: ButtonVariant,
  color: IconButtonColor,
  size: ButtonSize,
): CSSObject => ({
  ...radiusStyle(theme, variant, size),
  ...surfaceStyles(theme, paintFor(theme, color), size)[variant],
  // MUI hides the glyph under its spinner with this same rule; each surface's `&.Mui-disabled` colour
  // carries as many classes and is inserted later, so it has to be restated after them.
  "&.MuiIconButton-loading": { color: "transparent" },
});

/**
 * The radius lives here rather than in `MuiIconButton` so the icon buttons MUI renders inside
 * `Autocomplete` and `DateCalendar` keep their round shape.
 */
export const IconButtonRoot = styled(MuiIconButton, {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant: ButtonVariant }>(({ theme, variant, color = "primary", size }) =>
  iconButtonStyle(theme, variant, color, sizeName(size)),
);
